import { useState, useMemo, useEffect, useCallback } from "react";
import { DELIVERY_STATUS_STYLE, TYPE_COLORS, initials, avatarPalette } from "./data";
import { dbFetchSubscribers, dbFetchAreas, dbFetchRiders } from "./supabase";
import { resolveType, resolveLocation } from "./mealConfig";

const TODAY = new Date().toISOString().slice(0, 10);
const RIDER_PALETTE = ["#1D9E75","#534AB7","#D85A30","#BA7517","#185FA5","#A32D2D","#086375","#6B4226"];
const riderColor = (name, list) => RIDER_PALETTE[list.indexOf(name) % RIDER_PALETTE.length] || "#78716C";

const isDeliveryDay = (subscriber, dateStr) => {
  const dow = new Date(dateStr).getDay();
  const DAY_INDEX = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };
  const excSet = new Set((subscriber.excludedDays || []).map(d => DAY_INDEX[d]));
  return !excSet.has(dow);
};

const buildDeliveries = (subscribers, riders, dateStr) => {
  const entries = [];
  subscribers
    .filter(s => {
      // Inactive is always excluded — admin-controlled
      if (s.status === "Inactive") return false;
      // Trust Active/Pending/Overdue directly
      if (!["Active", "Pending", "Overdue", "Paused"].includes(s.status)) return false;
      if (!s.startDate || !s.endDate) return false;
      if (dateStr < s.startDate || dateStr > s.endDate) return false;
      if (!isDeliveryDay(s, dateStr)) return false;
      // If status is stored as "Paused", only exclude if a pause period actually
      // covers this date — otherwise an old/stale "Paused" status shouldn't hide
      // a subscriber who should receive a delivery today.
      if (s.status === "Paused") {
        const stillPausedToday = (s.pausePeriods || []).some(p => dateStr >= p.from && dateStr <= p.to);
        if (!stillPausedToday) return true; // stale status — show them anyway
      }
      return true;
    })
    .forEach(s => {
      const slots = s.mealSlots || ["Lunch"];
      slots.forEach(slot => {
        const paused = (s.pausePeriods || []).some(p =>
          dateStr >= p.from && dateStr <= p.to && (p.slots || []).includes(slot)
        );
        if (paused) return;
        const assignedRider = (s.slotRiders || {})[slot] || (riders[0] || "");
        const resolvedType = resolveType(s, slot, dateStr);
        const loc = resolveLocation(s, slot, dateStr);
        entries.push({
          id: `${s.id}_${slot}`, subscriberId: s.id,
          name: s.name, phone: s.phone, plan: s.plan, type: resolvedType,
          slot, deliveryArea: loc.area, notes: s.notes, amount: s.amount,
          locationKind: loc.kind, locationLabel: loc.label,
          date: dateStr, status: "Pending", deliveryTime: null,
          rider: assignedRider, failReason: null,
        });
      });
    });
  return entries;
};

// ── Build paused meals list for a given date ────────────────────────────────
const buildPauses = (subscribers, dateStr) => {
  const pauses = [];
  subscribers.forEach(s => {
    if (!s.pausePeriods || s.pausePeriods.length === 0) return;
    const mealSlots = s.mealSlots || ["Lunch"];
    // Find all pause periods that cover this date for this subscriber
    s.pausePeriods.forEach(p => {
      if (dateStr < p.from || dateStr > p.to) return;
      // Which of their slots are paused in this period?
      const pausedSlots = (p.slots || []).filter(sl => mealSlots.includes(sl));
      if (pausedSlots.length === 0) return;
      pauses.push({
        id:           `${s.id}_${p.id}`,
        subscriberId: s.id,
        name:         s.name,
        phone:        s.phone,
        deliveryArea: s.locationMode === "dual"
          ? `🏠 ${s.homeArea || "—"} / 🏢 ${s.officeArea || "—"}`
          : (s.deliveryArea || "—"),
        pausedSlots,
        from:         p.from,
        to:           p.to,
        notes:        s.notes,
        type:         (s.slotType?.[pausedSlots[0]]?.default) || s.type || "—",
        plan:         s.plan,
      });
    });
  });
  return pauses.sort((a, b) => a.name.localeCompare(b.name));
};

export default function Deliveries({ userId = "default" }) {
  const [areas,        setAreas]        = useState([]);
  const [riders,       setRiders]       = useState([]);
  const [subscribers,  setSubscribers]  = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [selectedDate, setSelectedDate] = useState(TODAY);
  const [deliveries,   setDeliveries]   = useState([]);
  const [filterArea,     setFilterArea]     = useState("All");
  const [filterStatus,   setFilterStatus]   = useState("All");
  const [filterRider,    setFilterRider]    = useState("All");
  const [filterMealSlot, setFilterMealSlot] = useState("All");
  const [search,         setSearch]         = useState("");
  const [groupBy,        setGroupBy]        = useState("area");
  const [showFailModal,  setShowFailModal]  = useState(null);
  const [failReason,     setFailReason]     = useState("");
  const [activeTab,      setActiveTab]      = useState("deliveries"); // "deliveries" | "pauses"
  const [pauseFilterSlot, setPauseFilterSlot] = useState("All");

  // Load from Supabase on mount
  useEffect(() => {
    Promise.all([
      dbFetchSubscribers(userId),
      dbFetchAreas(userId),
      dbFetchRiders(userId),
    ]).then(([subs, areaList, riderList]) => {
      const defaultAreas = ["Anna Nagar","T Nagar","Velachery","Adyar","Nungambakkam","Porur","Chromepet","Mylapore","Sholinganallur","Ambattur"];
      const defaultRiders = ["Rider 1","Rider 2","Rider 3"];
      setSubscribers(subs);
      setAreas(areaList.length > 0 ? areaList : defaultAreas);
      setRiders(riderList.length > 0 ? riderList : defaultRiders);
      setDeliveries(buildDeliveries(subs, riderList.length > 0 ? riderList : defaultRiders, TODAY));
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [userId]);

  const handleDateChange = useCallback((newDate) => {
    setSelectedDate(newDate);
    setDeliveries(buildDeliveries(subscribers, riders, newDate));
    setFilterArea("All"); setFilterStatus("All"); setFilterRider("All"); setFilterMealSlot("All"); setSearch("");
  }, [subscribers, riders]);

  const deliveryStatuses = ["Pending", "Out for Delivery", "Delivered", "Failed", "Skipped"];

  // ── Stats ───────────────────────────────────────────────────────────────────
  const stats = useMemo(() => ({
    total:     deliveries.length,
    delivered: deliveries.filter(d => d.status === "Delivered").length,
    outFor:    deliveries.filter(d => d.status === "Out for Delivery").length,
    pending:   deliveries.filter(d => d.status === "Pending").length,
    failed:    deliveries.filter(d => d.status === "Failed").length,
  }), [deliveries]);

  // ── Filtered ────────────────────────────────────────────────────────────────
  const filtered = useMemo(() => {
    return deliveries.filter(d => {
      const q = search.toLowerCase();
      if (q && !d.name.toLowerCase().includes(q) && !d.phone.includes(q) && !d.deliveryArea.toLowerCase().includes(q)) return false;
      if (filterArea     !== "All" && d.deliveryArea !== filterArea)              return false;
      if (filterStatus   !== "All" && d.status       !== filterStatus)            return false;
      if (filterRider    !== "All" && d.rider        !== filterRider)             return false;
      if (filterMealSlot !== "All" && d.slot !== filterMealSlot) return false;
      return true;
    });
  }, [deliveries, search, filterArea, filterStatus, filterRider, filterMealSlot]);

  // ── Grouped ─────────────────────────────────────────────────────────────────
  const grouped = useMemo(() => {
    const map = {};
    filtered.forEach(d => {
      const key = groupBy === "area" ? d.deliveryArea : groupBy === "rider" ? d.rider : d.status;
      if (!map[key]) map[key] = [];
      map[key].push(d);
    });
    return Object.entries(map).sort((a, b) => {
      if (groupBy === "status") {
        const order = ["Out for Delivery", "Pending", "Delivered", "Failed", "Skipped"];
        return order.indexOf(a[0]) - order.indexOf(b[0]);
      }
      return a[0].localeCompare(b[0]);
    });
  }, [filtered, groupBy]);

  // ── Handlers ────────────────────────────────────────────────────────────────
  const updateStatus = (id, status) => {
    if (status === "Failed") {
      setShowFailModal(id);
      return;
    }
    setDeliveries(prev => prev.map(d =>
      d.id === id ? {
        ...d, status,
        deliveryTime: status === "Delivered" ? new Date().toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" }) : d.deliveryTime,
        failReason: null,
      } : d
    ));
  };

  const confirmFail = () => {
    setDeliveries(prev => prev.map(d =>
      d.id === showFailModal ? { ...d, status: "Failed", failReason, deliveryTime: null } : d
    ));
    setShowFailModal(null);
    setFailReason("");
  };

  const markAllArea = (area, status) => {
    setDeliveries(prev => prev.map(d =>
      d.deliveryArea === area && d.status !== "Delivered"
        ? { ...d, status, deliveryTime: status === "Delivered" ? "Now" : d.deliveryTime }
        : d
    ));
  };

  const reassignRider = (id, newRider) => {
    setDeliveries(prev => prev.map(d => d.id === id ? { ...d, rider: newRider } : d));
  };

  const progressPct = Math.round(stats.total > 0 ? (stats.delivered / stats.total) * 100 : 0);

  // ── Pauses for selected date ─────────────────────────────────────────────
  const allPauses = useMemo(() => buildPauses(subscribers, selectedDate), [subscribers, selectedDate]);
  const filteredPauses = useMemo(() => {
    if (pauseFilterSlot === "All") return allPauses;
    return allPauses.filter(p => p.pausedSlots.includes(pauseFilterSlot));
  }, [allPauses, pauseFilterSlot]);

  if (loading) {
    return (
      <div style={{ fontFamily: "'DM Sans','Segoe UI',sans-serif", background: "#F7F6F2", minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div style={{ textAlign: "center", color: "#78716C", fontSize: 13 }}>Loading deliveries…</div>
      </div>
    );
  }

  const handlePrint = () => {
    // Build active filter summary for the print header
    const filterParts = [];
    if (filterArea      !== "All") filterParts.push(`Area: ${filterArea}`);
    if (filterStatus    !== "All") filterParts.push(`Status: ${filterStatus}`);
    if (filterRider     !== "All") filterParts.push(`Rider: ${filterRider}`);
    if (filterMealSlot  !== "All") filterParts.push(`Meal: ${filterMealSlot}`);
    if (search) filterParts.push(`Search: "${search}"`);
    const filterSummary = filterParts.length > 0 ? filterParts.join("  ·  ") : "All deliveries";

    // Group filtered rows by the active groupBy (area / rider / status) — matches screen view
    const printGroupMap = {};
    filtered.forEach(d => {
      const key = groupBy === "area" ? d.deliveryArea : groupBy === "rider" ? d.rider : d.status;
      if (!printGroupMap[key]) printGroupMap[key] = [];
      printGroupMap[key].push(d);
    });
    const printGroupEntries = Object.entries(printGroupMap).sort((a, b) => {
      if (groupBy === "status") {
        const order = ["Out for Delivery", "Pending", "Delivered", "Failed", "Skipped"];
        return order.indexOf(a[0]) - order.indexOf(b[0]);
      }
      return a[0].localeCompare(b[0]);
    });

    const rows = printGroupEntries.map(([groupKey, items]) => {
      const groupHeader = `
        <tr>
          <td colspan="9" style="background:#1C1917;color:#fff;padding:6px 10px;font-size:11px;font-weight:600;letter-spacing:0.04em;">
            ${groupBy === "area" ? "📍" : groupBy === "rider" ? "🛵" : "●"} ${groupKey} &nbsp;·&nbsp; ${items.length} deliveries
          </td>
        </tr>`;
      const dataRows = items.map(d => `
        <tr>
          <td>${d.name}</td>
          <td>${d.locationLabel || d.deliveryArea}</td>
          <td>${d.plan}</td>
          <td style="color:${TYPE_COLORS[d.type] || '#333'}">${d.type}</td>
          <td>${d.slot}</td>
          <td>${d.rider}</td>
          <td>${d.notes || "—"}</td>
          <td style="text-align:center;"><div style="width:18px;height:18px;border:1.5px solid #1C1917;border-radius:3px;margin:0 auto;"></div></td>
          <td style="text-align:center;"><div style="width:18px;height:18px;border:1.5px solid #1C1917;border-radius:3px;margin:0 auto;"></div></td>
        </tr>
      `).join("");
      return groupHeader + dataRows;
    }).join("");

    const win = window.open("", "_blank");
    win.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>MealBox Deliveries – ${selectedDate}</title>
        <style>
          * { box-sizing: border-box; margin: 0; padding: 0; }
          body { font-family: 'Segoe UI', sans-serif; font-size: 12px; color: #1a1a1a; padding: 24px; }
          .header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 20px; border-bottom: 1.5px solid #1C1917; padding-bottom: 14px; }
          .header h1 { font-size: 20px; font-weight: 700; }
          .header .meta { font-size: 12px; color: #78716C; margin-top: 4px; }
          .summary { display: flex; gap: 24px; margin-bottom: 16px; }
          .summary-item { background: #F7F6F2; border-radius: 6px; padding: 8px 14px; }
          .summary-item .val { font-size: 18px; font-weight: 700; }
          .summary-item .lbl { font-size: 10px; color: #78716C; margin-top: 2px; }
          .filters { font-size: 11px; color: #78716C; margin-bottom: 14px; padding: 6px 10px; background: #F7F6F2; border-radius: 6px; }
          table { width: 100%; border-collapse: collapse; font-size: 11.5px; }
          th { background: #1C1917; color: #FFF; padding: 8px 10px; text-align: left; font-weight: 500; font-size: 11px; }
          td { padding: 7px 10px; border-bottom: 0.5px solid #E7E5E0; vertical-align: middle; }
          tr:nth-child(even) td { background: #FAFAF9; }
          .footer { margin-top: 20px; font-size: 10px; color: #A8A29E; text-align: right; border-top: 0.5px solid #E7E5E0; padding-top: 10px; }
          @media print {
            body { padding: 12px; }
            button { display: none !important; }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <div>
            <h1>MealBox – Delivery Sheet</h1>
            <div class="meta">Date: ${selectedDate} &nbsp;·&nbsp; Grouped by: ${groupBy.charAt(0).toUpperCase() + groupBy.slice(1)} &nbsp;·&nbsp; Printed: ${new Date().toLocaleString("en-IN")}</div>
          </div>
          <button onclick="window.print()" style="padding:8px 18px;background:#1C1917;color:#fff;border:none;border-radius:6px;font-size:13px;cursor:pointer;">
            🖨 Print
          </button>
        </div>
        <div class="summary">
          <div class="summary-item"><div class="val">${filtered.length}</div><div class="lbl">Total shown</div></div>
          <div class="summary-item"><div class="val" style="color:#27500A">${filtered.filter(d=>d.status==="Delivered").length}</div><div class="lbl">Delivered</div></div>
          <div class="summary-item"><div class="val" style="color:#0C447C">${filtered.filter(d=>d.status==="Out for Delivery").length}</div><div class="lbl">Out for delivery</div></div>
          <div class="summary-item"><div class="val" style="color:#633806">${filtered.filter(d=>d.status==="Pending").length}</div><div class="lbl">Pending</div></div>
          <div class="summary-item"><div class="val" style="color:#791F1F">${filtered.filter(d=>d.status==="Failed").length}</div><div class="lbl">Failed</div></div>
        </div>
        <div class="filters">Filters applied: ${filterSummary}</div>
        <table>
          <thead>
            <tr>
              <th>Name</th><th>Location</th><th>Plan</th>
              <th>Type</th><th>Meal Slots</th><th>Rider</th><th>Notes</th>
              <th style="text-align:center;min-width:70px;">Meal Packed</th>
              <th style="text-align:center;min-width:80px;">Out for Delivery</th>
            </tr>
          </thead>
          <tbody>${rows}</tbody>
        </table>
        <div class="footer">MealBox Business Console &nbsp;·&nbsp; ${filtered.length} of ${deliveries.length} deliveries printed</div>
        <script>
          // Auto-open print dialog after a short delay
          setTimeout(() => window.print(), 400);
        </script>
      </body>
      </html>
    `);
    win.document.close();
  };

  // ── Print pauses list ────────────────────────────────────────────────────
  const handlePrintPauses = () => {
    const slotFilterLabel = pauseFilterSlot !== "All" ? `Meal: ${pauseFilterSlot}` : "All meals";

    const rows = filteredPauses.map((p) => {
      const isSingleDay = p.from === p.to;
      const SLOT_COLORS_MAP = { Breakfast: "#BA7517", Lunch: "#1D9E75", Dinner: "#534AB7" };
      return `
        <tr>
          <td>${p.name}</td>
          <td>${p.deliveryArea || "—"}</td>
          <td>${p.pausedSlots.map(sl => `<span style="display:inline-block;font-size:10.5px;font-weight:600;padding:2px 8px;border-radius:10px;background:${SLOT_COLORS_MAP[sl]}18;color:${SLOT_COLORS_MAP[sl]};margin-right:3px;">${sl}</span>`).join("")}</td>
          <td>${isSingleDay ? p.from : `${p.from} &rarr; ${p.to}`}</td>
          <td>${p.plan} / ${p.type}</td>
          <td>${p.notes || "—"}</td>
        </tr>`;
    }).join("");

    const win = window.open("", "_blank");
    win.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>MealBox Pauses – ${selectedDate}</title>
        <style>
          * { box-sizing: border-box; margin: 0; padding: 0; }
          body { font-family: 'Segoe UI', sans-serif; font-size: 12px; color: #1a1a1a; padding: 24px; }
          .header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 20px; border-bottom: 1.5px solid #1C1917; padding-bottom: 14px; }
          .header h1 { font-size: 20px; font-weight: 700; }
          .header .meta { font-size: 12px; color: #78716C; margin-top: 4px; }
          .summary { display: flex; gap: 24px; margin-bottom: 16px; }
          .summary-item { background: #F7F6F2; border-radius: 6px; padding: 8px 14px; }
          .summary-item .val { font-size: 18px; font-weight: 700; }
          .summary-item .lbl { font-size: 10px; color: #78716C; margin-top: 2px; }
          .filters { font-size: 11px; color: #78716C; margin-bottom: 14px; padding: 6px 10px; background: #F7F6F2; border-radius: 6px; }
          table { width: 100%; border-collapse: collapse; font-size: 11.5px; }
          th { background: #1C1917; color: #FFF; padding: 8px 10px; text-align: left; font-weight: 500; font-size: 11px; }
          td { padding: 7px 10px; border-bottom: 0.5px solid #E7E5E0; vertical-align: middle; }
          tr:nth-child(even) td { background: #FAFAF9; }
          .footer { margin-top: 20px; font-size: 10px; color: #A8A29E; text-align: right; border-top: 0.5px solid #E7E5E0; padding-top: 10px; }
          @media print {
            body { padding: 12px; }
            button { display: none !important; }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <div>
            <h1>MealBox – Paused Subscribers</h1>
            <div class="meta">Date: ${selectedDate} &nbsp;&middot;&nbsp; Printed: ${new Date().toLocaleString("en-IN")}</div>
          </div>
          <button onclick="window.print()" style="padding:8px 18px;background:#1C1917;color:#fff;border:none;border-radius:6px;font-size:13px;cursor:pointer;">
            &#128424; Print
          </button>
        </div>
        <div class="summary">
          <div class="summary-item"><div class="val">${filteredPauses.length}</div><div class="lbl">Total paused</div></div>
          <div class="summary-item"><div class="val" style="color:#BA7517">${allPauses.filter(p => p.pausedSlots.includes("Breakfast")).length}</div><div class="lbl">Breakfast</div></div>
          <div class="summary-item"><div class="val" style="color:#1D9E75">${allPauses.filter(p => p.pausedSlots.includes("Lunch")).length}</div><div class="lbl">Lunch</div></div>
          <div class="summary-item"><div class="val" style="color:#534AB7">${allPauses.filter(p => p.pausedSlots.includes("Dinner")).length}</div><div class="lbl">Dinner</div></div>
        </div>
        <div class="filters">Filter applied: ${slotFilterLabel}</div>
        <table>
          <thead>
            <tr>
              <th>Subscriber</th><th>Area</th>
              <th>Paused Meals</th><th>Pause Period</th><th>Plan / Type</th><th>Notes</th>
            </tr>
          </thead>
          <tbody>${rows}</tbody>
        </table>
        <div class="footer">MealBox Business Console &nbsp;&middot;&nbsp; ${filteredPauses.length} paused subscriber${filteredPauses.length !== 1 ? "s" : ""} printed</div>
        <script>
          setTimeout(() => window.print(), 400);
        </script>
      </body>
      </html>
    `);
    win.document.close();
  };

  return (
    <div className="page-wrap" style={{ fontFamily: "'DM Sans','Segoe UI',sans-serif", background: "#F7F6F2", minHeight: "100vh" }}>

      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "1.5rem" }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 600, letterSpacing: "-0.02em", margin: 0 }}>Deliveries</h1>
          <p style={{ fontSize: 13, color: "#78716C", margin: "4px 0 0" }}>
            {activeTab === "deliveries"
              ? `${filtered.length} of ${deliveries.length} shown · ${selectedDate}`
              : `${filteredPauses.length} paused meal${filteredPauses.length !== 1 ? "s" : ""} · ${selectedDate}`}
          </p>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          {/* Tab toggle */}
          <div style={{ display: "flex", background: "#F3F2EE", borderRadius: 8, padding: 3, gap: 2 }}>
            {[
              { key: "deliveries", label: "◉ Deliveries" },
              { key: "pauses",     label: "⏸ Pauses" + (allPauses.length > 0 ? ` (${allPauses.length})` : "") },
            ].map(t => (
              <button key={t.key} onClick={() => setActiveTab(t.key)} style={{
                fontSize: 12, padding: "5px 14px", borderRadius: 6, border: "none", cursor: "pointer", fontWeight: 500,
                background: activeTab === t.key ? "#FFF" : "transparent",
                color:      activeTab === t.key ? "#1C1917" : "#78716C",
                boxShadow:  activeTab === t.key ? "0 1px 3px rgba(0,0,0,0.08)" : "none",
                transition: "all 0.15s",
              }}>{t.label}</button>
            ))}
          </div>
          {activeTab === "deliveries" && (
            <button onClick={handlePrint} style={{ display: "flex", alignItems: "center", gap: 6, padding: "7px 16px", fontSize: 13, borderRadius: 8, border: "0.5px solid #E7E5E0", background: "#FFF", cursor: "pointer", color: "#44403C", fontWeight: 500 }}>
              🖨 Print / Save PDF
            </button>
          )}
          {activeTab === "pauses" && filteredPauses.length > 0 && (
            <button onClick={handlePrintPauses} style={{ display: "flex", alignItems: "center", gap: 6, padding: "7px 16px", fontSize: 13, borderRadius: 8, border: "0.5px solid #E7E5E0", background: "#FFF", cursor: "pointer", color: "#44403C", fontWeight: 500 }}>
              🖨 Print Pauses
            </button>
          )}
          <input type="date" value={selectedDate} onChange={e => handleDateChange(e.target.value)}
            style={{ padding: "7px 12px", fontSize: 13, borderRadius: 8, border: "0.5px solid #E7E5E0", background: "#FFF", outline: "none", cursor: "pointer" }} />
        </div>
      </div>

      {/* Progress bar */}
      <div style={{ background: "#FFF", borderRadius: 12, border: "0.5px solid #E7E5E0", padding: "1.25rem", marginBottom: "1.25rem" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
          <div style={{ fontSize: 13, fontWeight: 500, color: "#1C1917" }}>Today's delivery progress</div>
          <div style={{ fontSize: 13, fontWeight: 600, color: "#1C1917" }}>{progressPct}% complete</div>
        </div>
        <div style={{ height: 8, background: "#F3F2EE", borderRadius: 4, overflow: "hidden", marginBottom: 12 }}>
          <div style={{ height: "100%", width: `${progressPct}%`, background: "#1C1917", borderRadius: 4, transition: "width 0.4s ease" }} />
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 8 }}>
          {[
            { label: "Delivered",        value: stats.delivered, color: "#27500A", bg: "#EAF3DE" },
            { label: "Out for delivery", value: stats.outFor,    color: "#0C447C", bg: "#E6F1FB" },
            { label: "Pending",          value: stats.pending,   color: "#633806", bg: "#FAEEDA" },
            { label: "Failed",           value: stats.failed,    color: "#791F1F", bg: "#FCEBEB" },
          ].map(c => (
            <div key={c.label} style={{ background: c.bg, borderRadius: 8, padding: "10px 14px", cursor: "pointer" }}
              onClick={() => setFilterStatus(filterStatus === c.label ? "All" : c.label)}>
              <div style={{ fontSize: 20, fontWeight: 600, color: c.color }}>{c.value}</div>
              <div style={{ fontSize: 11, color: c.color, marginTop: 2, opacity: 0.8 }}>{c.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* ── Deliveries tab ─────────────────────────────────────────────────── */}
      {activeTab === "deliveries" && <>

      {/* Filters row */}
      <div className="filter-row" style={{ marginBottom: "1rem" }}>
        <input value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Search name, phone, area…"
          style={{ flex: "1 1 180px", padding: "7px 12px", fontSize: 13, borderRadius: 8, border: "0.5px solid #E7E5E0", background: "#FFF", outline: "none" }} />

        {[
          { label: "Area",   val: filterArea,   set: setFilterArea,   opts: ["All", ...areas] },
          { label: "Status", val: filterStatus, set: setFilterStatus, opts: ["All", ...deliveryStatuses] },
          { label: "Rider",  val: filterRider,  set: setFilterRider,  opts: ["All", ...riders] },
          { label: "Meal",   val: filterMealSlot, set: setFilterMealSlot, opts: ["All", "Breakfast", "Lunch", "Dinner"] },
        ].map(f => (
          <select key={f.label} value={f.val} onChange={e => f.set(e.target.value)}
            style={{ padding: "7px 10px", fontSize: 13, borderRadius: 8, border: "0.5px solid #E7E5E0", background: "#FFF", cursor: "pointer", outline: "none" }}>
            {f.opts.map(o => <option key={o} value={o}>{o === "All" ? `${f.label}: All` : o}</option>)}
          </select>
        ))}

        <div style={{ marginLeft: "auto", display: "flex", gap: 4 }}>
          {["area", "rider", "status"].map(g => (
            <button key={g} onClick={() => setGroupBy(g)} style={{
              fontSize: 12, padding: "6px 12px", borderRadius: 8, cursor: "pointer",
              border: "0.5px solid #E7E5E0",
              background: groupBy === g ? "#1C1917" : "#FFF",
              color: groupBy === g ? "#FFF" : "#78716C",
              textTransform: "capitalize",
            }}>By {g}</button>
          ))}
        </div>
      </div>

      {/* Delivery groups */}
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {grouped.length === 0 && (
          <div style={{ background: "#FFF", borderRadius: 12, border: "0.5px solid #E7E5E0", padding: "3rem", textAlign: "center", color: "#A8A29E", fontSize: 13 }}>
            No deliveries found
          </div>
        )}
        {grouped.map(([groupKey, items]) => {
          const doneCount = items.filter(d => d.status === "Delivered").length;
          const st = DELIVERY_STATUS_STYLE[groupKey] || {};
          return (
            <div key={groupKey} style={{ background: "#FFF", borderRadius: 12, border: "0.5px solid #E7E5E0", overflow: "hidden" }}>
              {/* Group header */}
              <div style={{ padding: "10px 16px", background: "#F7F6F2", borderBottom: "0.5px solid #E7E5E0", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  {groupBy === "status" && (
                    <span style={{ fontSize: 11, fontWeight: 500, padding: "2px 8px", borderRadius: 20, background: st.bg, color: st.color }}>{groupKey}</span>
                  )}
                  {groupBy === "rider" && (
                    <span style={{ width: 8, height: 8, borderRadius: "50%", background: riderColor(groupKey, riders) || "#888", display: "inline-block" }} />
                  )}
                  <span style={{ fontSize: 13, fontWeight: 500, color: "#1C1917" }}>
                    {groupBy !== "status" ? groupKey : ""}
                  </span>
                  <span style={{ fontSize: 12, color: "#A8A29E" }}>
                    {doneCount}/{items.length} delivered
                  </span>
                </div>
                {groupBy === "area" && (
                  <button onClick={() => markAllArea(groupKey, "Delivered")}
                    style={{ fontSize: 11, padding: "4px 10px", borderRadius: 6, border: "0.5px solid #E7E5E0", background: "#FFF", cursor: "pointer", color: "#44403C" }}>
                    Mark all delivered
                  </button>
                )}
              </div>

              {/* Delivery rows */}
              <div className="delivery-table-wrap"><table className="delivery-table" style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                <tbody>
                  {items.map((d, i) => {
                    const av  = avatarPalette(d.subscriberId);
                    const dst = DELIVERY_STATUS_STYLE[d.status] || { bg: "#F1EFE8", color: "#5F5E5A" };
                    return (
                      <tr key={d.id} style={{ borderBottom: i < items.length - 1 ? "0.5px solid #F3F2EE" : "none" }}>
                        {/* Subscriber */}
                        <td style={{ padding: "10px 16px", width: 200 }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                            <div style={{ width: 30, height: 30, borderRadius: "50%", background: av.bg, color: av.color, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 500, flexShrink: 0 }}>
                              {initials(d.name)}
                            </div>
                            <div>
                              <div style={{ fontWeight: 500, color: "#1C1917" }}>{d.name}</div>
                              <div style={{ fontSize: 11, color: "#A8A29E" }}>{d.phone}</div>
                            </div>
                          </div>
                        </td>

                        {/* Plan & type */}
                        <td style={{ padding: "10px 8px", color: "#78716C" }}>
                          <div style={{ fontSize: 12 }}>{d.plan}</div>
                          <div style={{ fontSize: 11, color: TYPE_COLORS[d.type], fontWeight: 500, marginTop: 2 }}>● {d.type}</div>
                        </td>

                        {/* Meal slot */}
                        <td style={{ padding: "10px 8px" }}>
                          {(() => {
                            const slotStyle = {
                              Breakfast: { bg: "#FAEEDA", color: "#633806" },
                              Lunch:     { bg: "#EAF3DE", color: "#27500A" },
                              Dinner:    { bg: "#EEEDFE", color: "#3C3489" },
                            }[d.slot] || { bg: "#F1EFE8", color: "#5F5E5A" };
                            return (
                              <span style={{ fontSize: 11, fontWeight: 500, padding: "3px 10px", borderRadius: 20, background: slotStyle.bg, color: slotStyle.color, whiteSpace: "nowrap" }}>
                                {d.slot}
                              </span>
                            );
                          })()}
                        </td>

                        {/* Area */}
                        <td style={{ padding: "10px 8px", fontSize: 12, color: "#78716C" }}>{d.locationLabel || d.deliveryArea}</td>

                        {/* Rider — inline reassign dropdown */}
                        <td style={{ padding: "10px 8px", minWidth: 130 }}>
                          <select value={d.rider || ""} onChange={e => reassignRider(d.id, e.target.value)}
                            style={{ fontSize: 12, padding: "4px 6px", borderRadius: 6, border: `0.5px solid ${d.rider ? riderColor(d.rider, riders) : "#E7E5E0"}`, background: "#FFF", color: d.rider ? riderColor(d.rider, riders) : "#A8A29E", cursor: "pointer", outline: "none", fontWeight: 500, maxWidth: 130 }}>
                            <option value="">— Unassigned —</option>
                            {riders.map(r => <option key={r} value={r}>{r}</option>)}
                          </select>
                        </td>

                        {/* Notes */}
                        <td style={{ padding: "10px 8px", maxWidth: 120 }}>
                          {d.notes ? <span style={{ fontSize: 11, color: "#78716C", fontStyle: "italic" }}>{d.notes}</span> : null}
                          {d.failReason ? <span style={{ fontSize: 11, color: "#A32D2D" }}>⚠ {d.failReason}</span> : null}
                        </td>

                        {/* Delivery time */}
                        <td style={{ padding: "10px 8px", fontSize: 11, color: "#A8A29E", whiteSpace: "nowrap" }}>
                          {d.deliveryTime || "—"}
                        </td>

                        {/* Status + quick actions */}
                        <td style={{ padding: "10px 16px 10px 8px" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 6, justifyContent: "flex-end" }}>
                            <span style={{ fontSize: 11, fontWeight: 500, padding: "3px 10px", borderRadius: 20, background: dst.bg, color: dst.color, whiteSpace: "nowrap" }}>
                              {d.status}
                            </span>
                            {d.status !== "Delivered" && (
                              <button onClick={() => updateStatus(d.id, "Delivered")}
                                style={{ fontSize: 11, padding: "3px 8px", borderRadius: 6, border: "0.5px solid #9FE1CB", background: "#E1F5EE", color: "#085041", cursor: "pointer", whiteSpace: "nowrap" }}>
                                ✓ Done
                              </button>
                            )}
                            {d.status === "Pending" && (
                              <button onClick={() => updateStatus(d.id, "Out for Delivery")}
                                style={{ fontSize: 11, padding: "3px 8px", borderRadius: 6, border: "0.5px solid #B5D4F4", background: "#E6F1FB", color: "#0C447C", cursor: "pointer", whiteSpace: "nowrap" }}>
                                → Out
                              </button>
                            )}
                            {d.status !== "Delivered" && d.status !== "Failed" && (
                              <button onClick={() => updateStatus(d.id, "Failed")}
                                style={{ fontSize: 11, padding: "3px 8px", borderRadius: 6, border: "0.5px solid #F7C1C1", background: "#FCEBEB", color: "#791F1F", cursor: "pointer" }}>
                                ✕
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table></div>
            </div>
          );
        })}
      </div>

      </>}

      {/* ── Pauses tab ──────────────────────────────────────────────────────── */}
      {activeTab === "pauses" && (
        <div>
          {/* Meal slot filter pills */}
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: "1rem" }}>
            <span style={{ fontSize: 12, color: "#78716C", fontWeight: 500 }}>Filter by meal:</span>
            {["All", "Breakfast", "Lunch", "Dinner"].map(slot => {
              const slotColor = { Breakfast: "#BA7517", Lunch: "#1D9E75", Dinner: "#534AB7" }[slot];
              const active = pauseFilterSlot === slot;
              return (
                <button key={slot} onClick={() => setPauseFilterSlot(slot)} style={{
                  fontSize: 12, padding: "5px 14px", borderRadius: 20, cursor: "pointer",
                  border: `0.5px solid ${active ? (slotColor || "#1C1917") : "#E7E5E0"}`,
                  background: active ? (slotColor ? slotColor + "18" : "#1C1917") : "#FFF",
                  color: active ? (slotColor || "#FFF") : "#78716C",
                  fontWeight: active ? 600 : 400,
                  transition: "all 0.15s",
                }}>{slot}</button>
              );
            })}
            <span style={{ marginLeft: "auto", fontSize: 12, color: "#A8A29E" }}>
              {filteredPauses.length} pause{filteredPauses.length !== 1 ? "s" : ""} on {selectedDate}
            </span>
          </div>

          {/* Pauses table */}
          {filteredPauses.length === 0 ? (
            <div style={{ background: "#FFF", borderRadius: 12, border: "0.5px solid #E7E5E0", padding: "3rem", textAlign: "center" }}>
              <div style={{ fontSize: 24, marginBottom: 10 }}>✓</div>
              <div style={{ fontSize: 14, fontWeight: 500, color: "#1C1917", marginBottom: 4 }}>No pauses for this day</div>
              <div style={{ fontSize: 13, color: "#A8A29E" }}>
                {pauseFilterSlot !== "All" ? `No ${pauseFilterSlot} pauses on ${selectedDate}` : `All subscribers are active on ${selectedDate}`}
              </div>
            </div>
          ) : (
            <div style={{ background: "#FFF", borderRadius: 12, border: "0.5px solid #E7E5E0", overflow: "hidden" }}>
              {/* Summary strip */}
              <div style={{ padding: "10px 16px", background: "#FFF8F0", borderBottom: "0.5px solid #E7E5E0", display: "flex", gap: 20 }}>
                {["Breakfast", "Lunch", "Dinner"].map(slot => {
                  const count = allPauses.filter(p => p.pausedSlots.includes(slot)).length;
                  const slotColor = { Breakfast: "#BA7517", Lunch: "#1D9E75", Dinner: "#534AB7" }[slot];
                  return count > 0 ? (
                    <div key={slot} style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <span style={{ fontSize: 11, fontWeight: 600, padding: "2px 8px", borderRadius: 10, background: slotColor + "18", color: slotColor }}>{slot}</span>
                      <span style={{ fontSize: 12, color: "#78716C" }}>{count} paused</span>
                    </div>
                  ) : null;
                })}
              </div>

              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                <thead>
                  <tr style={{ background: "#F7F6F2", borderBottom: "0.5px solid #E7E5E0" }}>
                    {["Subscriber", "Area", "Paused Meals", "Pause Period", "Plan / Type", "Notes"].map(h => (
                      <th key={h} style={{ padding: "10px 12px", textAlign: "left", fontWeight: 500, fontSize: 12, color: "#78716C", whiteSpace: "nowrap" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filteredPauses.map((p, i) => {
                    const av = avatarPalette(p.subscriberId);
                    const SLOT_COLORS = { Breakfast: "#BA7517", Lunch: "#1D9E75", Dinner: "#534AB7" };
                    const TYPE_COLORS_MAP = { Veg: "#1D9E75", "Non-Veg": "#D85A30", Vegan: "#534AB7", Jain: "#BA7517" };
                    const isSingleDay = p.from === p.to;
                    return (
                      <tr key={p.id} style={{ borderBottom: i < filteredPauses.length - 1 ? "0.5px solid #F3F2EE" : "none", background: "#FFF" }}>
                        {/* Subscriber */}
                        <td style={{ padding: "10px 12px" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                            <div style={{ width: 30, height: 30, borderRadius: "50%", background: av.bg, color: av.color, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 500, flexShrink: 0 }}>
                              {initials(p.name)}
                            </div>
                            <div>
                              <div style={{ fontWeight: 500, color: "#1C1917" }}>{p.name}</div>
                              <div style={{ fontSize: 11, color: "#A8A29E" }}>{p.phone}</div>
                            </div>
                          </div>
                        </td>
                        {/* Area */}
                        <td style={{ padding: "10px 12px", fontSize: 12, color: "#78716C" }}>{p.deliveryArea}</td>
                        {/* Paused meal slots */}
                        <td style={{ padding: "10px 12px" }}>
                          <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                            {p.pausedSlots.map(sl => (
                              <span key={sl} style={{ fontSize: 11, fontWeight: 500, padding: "3px 10px", borderRadius: 20, background: (SLOT_COLORS[sl] || "#78716C") + "18", color: SLOT_COLORS[sl] || "#78716C", whiteSpace: "nowrap" }}>
                                {sl}
                              </span>
                            ))}
                          </div>
                        </td>
                        {/* Pause period */}
                        <td style={{ padding: "10px 12px", fontSize: 12 }}>
                          {isSingleDay ? (
                            <span style={{ color: "#1C1917" }}>{p.from}</span>
                          ) : (
                            <span style={{ color: "#1C1917" }}>{p.from} <span style={{ color: "#A8A29E" }}>→</span> {p.to}</span>
                          )}
                        </td>
                        {/* Plan / type */}
                        <td style={{ padding: "10px 12px" }}>
                          <div style={{ fontSize: 12, color: "#44403C" }}>{p.plan}</div>
                          <div style={{ fontSize: 11, color: TYPE_COLORS_MAP[p.type] || "#78716C", fontWeight: 500, marginTop: 2 }}>● {p.type}</div>
                        </td>
                        {/* Notes */}
                        <td style={{ padding: "10px 12px", fontSize: 11, color: "#78716C", fontStyle: p.notes ? "italic" : "normal" }}>
                          {p.notes || "—"}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── Fail reason modal ── */}
      {showFailModal && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}>
          <div className="modal-sm" style={{ background: "#FFF", borderRadius: 16, padding: "2rem" }}>
            <h3 style={{ fontSize: 16, fontWeight: 600, margin: "0 0 1rem" }}>Mark as failed</h3>
            <p style={{ fontSize: 13, color: "#78716C", marginBottom: 12 }}>Please provide a reason for the failed delivery:</p>
            <select value={failReason} onChange={e => setFailReason(e.target.value)}
              style={{ width: "100%", padding: "8px 10px", fontSize: 13, borderRadius: 8, border: "0.5px solid #E7E5E0", marginBottom: 16, outline: "none" }}>
              <option value="">Select reason…</option>
              <option>Customer not available</option>
              <option>Wrong address</option>
              <option>Customer refused delivery</option>
              <option>Rider could not locate address</option>
              <option>Other</option>
            </select>
            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={confirmFail} disabled={!failReason}
                style={{ flex: 1, padding: "9px", fontSize: 13, fontWeight: 500, borderRadius: 8, border: "none", background: failReason ? "#1C1917" : "#E7E5E0", color: failReason ? "#FFF" : "#A8A29E", cursor: failReason ? "pointer" : "default" }}>
                Confirm
              </button>
              <button onClick={() => setShowFailModal(null)}
                style={{ padding: "9px 18px", fontSize: 13, borderRadius: 8, border: "0.5px solid #E7E5E0", background: "#FFF", cursor: "pointer" }}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
